import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, apiError, fmtDate, API } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Plus, X, Upload, AlertTriangle, Trash2, Edit3, Link2, FileText, Lock } from "lucide-react";

const cur_month_ist = () => {
    const now = new Date();
    const ist = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    return `${ist.getFullYear()}-${String(ist.getMonth() + 1).padStart(2, "0")}`;
};

export default function ExpensesPage() {
    const { user } = useAuth();
    const { siteId } = useParams();
    const nav = useNavigate();
    const [site, setSite] = useState(null);
    const [month, setMonth] = useState(cur_month_ist());
    const [rows, setRows] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [categories, setCategories] = useState([]);
    const [lock, setLock] = useState({ locked: false });
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);   // expense being edited, or 'new'

    const isHO = user?.role === "HO_ADMIN";
    const isPM = user?.role === "PROJECT_MANAGER";
    const currentMonth = cur_month_ist();
    const isCurrentMonth = month === currentMonth;
    const canWrite = !lock.locked && (isHO || (user?.role === "SITE_MANAGER" && isCurrentMonth) || isPM);

    async function load() {
        try {
            setLoading(true);
            const [s, e, v, c, l] = await Promise.all([
                api.get(`/sites/${siteId}`),
                api.get(`/sites/${siteId}/expenses`, { params: { month } }),
                api.get("/vendors"),
                api.get("/expense-categories"),
                api.get(`/sites/${siteId}/expenses/lock-status`, { params: { month } }),
            ]);
            setSite(s.data);
            setRows(e.data);
            setVendors(v.data);
            setCategories(c.data);
            setLock(l.data);
        } catch (er) {
            toast.error(apiError(er));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load(); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteId, month]);

    const total = useMemo(() => rows.reduce((a, r) => a + (r.amount || 0), 0), [rows]);
    const missing = useMemo(() => rows.filter((r) => r.invoice_pending || !r.invoice_file_id).length, [rows]);

    async function del(id) {
        if (!window.confirm("Delete this expense?")) return;
        try {
            await api.delete(`/expenses/${id}`);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    async function finalise() {
        if (!window.confirm(`Finalise ${month} — this locks all entries. Continue?`)) return;
        try {
            await api.post(`/sites/${siteId}/expenses/finalise`, null, { params: { month } });
            toast.success("Month finalised");
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    async function unlock() {
        if (!window.confirm(`Unlock ${month}? This will be audit-logged.`)) return;
        try {
            await api.post(`/sites/${siteId}/expenses/unlock`, null, { params: { month } });
            toast.success("Month unlocked");
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500">Expenses · {site?.name || "…"}</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">Site expenses</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Unified expense entry. Attach an invoice or mark it pending — pending &gt;7 days will nag you here and via notifications.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <input
                        type="month"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                        className="rounded-md border border-gray-200 px-3 py-2 text-sm"
                        data-testid="expenses-month-picker"
                    />
                    <Link to={`/sites/${siteId}/expenses/report?month=${month}`}
                        className="rounded-md bg-white border border-gray-200 px-3 py-2 text-sm font-medium hover:bg-gray-50"
                        data-testid="btn-open-monthly-report">
                        Monthly report
                    </Link>
                    <Link to={`/sites/${siteId}/billable-log`}
                        className="rounded-md bg-white border border-gray-200 px-3 py-2 text-sm font-medium hover:bg-gray-50"
                        data-testid="btn-open-billable-log">
                        Billable log
                    </Link>
                    {isHO && !lock.locked && (
                        <button onClick={finalise} className="rounded-md bg-[#1E3355] text-white px-3 py-2 text-sm font-medium hover:opacity-90" data-testid="btn-finalise-month">
                            Finalise month
                        </button>
                    )}
                    {isHO && lock.locked && (
                        <button onClick={unlock} className="rounded-md bg-amber-100 text-amber-800 border border-amber-200 px-3 py-2 text-sm font-medium hover:bg-amber-200" data-testid="btn-unlock-month">
                            <Lock className="inline w-4 h-4 mr-1" />Unlock
                        </button>
                    )}
                    {canWrite && (
                        <button onClick={() => setEditing("new")} className="rounded-md bg-[#E02B2B] text-white px-3 py-2 text-sm font-medium hover:opacity-90" data-testid="btn-add-expense">
                            <Plus className="inline w-4 h-4 mr-1" />Add expense
                        </button>
                    )}
                </div>
            </div>

            {lock.locked && (
                <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-4 py-2 text-sm flex items-center gap-2" data-testid="month-locked-banner">
                    <Lock className="w-4 h-4" /> Month finalised on {fmtDate(lock.lock?.locked_at)} — entries are locked.
                </div>
            )}

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Entries" value={rows.length} testId="stat-count" />
                <StatCard label="Total ₹" value={total.toLocaleString("en-IN")} testId="stat-total" />
                <StatCard label="Missing invoice" value={missing} testId="stat-missing" tone={missing > 0 ? "warn" : "ok"} />
                <StatCard label="Month" value={month} testId="stat-month" />
            </div>

            <div className="mt-6 rounded-xl bg-white border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-[#F4F6F9] text-gray-500 text-xs uppercase tracking-wider">
                            <tr>
                                <Th>Date</Th><Th>Vendor</Th><Th>Category</Th><Th>Description</Th>
                                <Th className="text-right">Amount ₹</Th><Th>Invoice</Th><Th>Flags</Th><Th></Th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr><td colSpan={8} className="p-6 text-center text-gray-400">Loading…</td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={8} className="p-6 text-center text-gray-400" data-testid="no-expenses">No entries for this month.</td></tr>
                            ) : rows.map((r) => (
                                <tr key={r.id} className="hover:bg-gray-50" data-testid={`expense-row-${r.id}`}>
                                    <Td>{fmtDate(r.date)}</Td>
                                    <Td className="font-medium text-[#1E3355]">{r.vendor_name}</Td>
                                    <Td>{r.category_name}</Td>
                                    <Td className="max-w-[280px] truncate" title={r.description}>{r.description}</Td>
                                    <Td className="text-right tabular-nums">{Number(r.amount).toLocaleString("en-IN")}</Td>
                                    <Td>
                                        {r.invoice_file_id ? (
                                            <a
                                                className="text-[#1E3355] hover:underline inline-flex items-center gap-1"
                                                href={`${API}/expense-invoice-files/${r.invoice_file_id}/blob`}
                                                target="_blank" rel="noopener noreferrer"
                                                data-testid={`invoice-link-${r.id}`}
                                            >
                                                <FileText className="w-3.5 h-3.5" /> View
                                            </a>
                                        ) : r.invoice_pending ? (
                                            <span className="inline-flex items-center gap-1 text-amber-700 text-xs">
                                                <AlertTriangle className="w-3.5 h-3.5" /> Pending
                                            </span>
                                        ) : "—"}
                                    </Td>
                                    <Td className="text-xs">
                                        {r.is_recoverable && <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100 mr-1">Recoverable</span>}
                                        {r.source === "import" && <span className="inline-block px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100 mr-1">Imported</span>}
                                        {r.invoice_number ? "" : <span className="text-gray-400">no inv#</span>}
                                    </Td>
                                    <Td className="text-right whitespace-nowrap">
                                        {canWrite && (
                                            <>
                                                <button onClick={() => setEditing(r)} className="text-gray-500 hover:text-[#1E3355] mr-2" data-testid={`edit-expense-${r.id}`}>
                                                    <Edit3 className="w-4 h-4 inline" />
                                                </button>
                                                <button onClick={() => del(r.id)} className="text-gray-500 hover:text-red-600" data-testid={`del-expense-${r.id}`}>
                                                    <Trash2 className="w-4 h-4 inline" />
                                                </button>
                                            </>
                                        )}
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {editing && (
                <ExpenseForm
                    site={site}
                    expense={editing === "new" ? null : editing}
                    vendors={vendors}
                    categories={categories}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                    onVendorsChanged={async () => { const v = await api.get("/vendors"); setVendors(v.data); }}
                    defaultMonth={month}
                />
            )}
        </div>
    );
}

function Th({ children, className = "" }) {
    return <th className={`px-3 py-2 text-left ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
    return <td className={`px-3 py-2 ${className}`}>{children}</td>;
}
function StatCard({ label, value, testId, tone = "ok" }) {
    const toneCls = tone === "warn" ? "text-amber-700 bg-amber-50 border-amber-100" : "text-[#1E3355] bg-white border-gray-100";
    return (
        <div className={`rounded-xl border p-4 ${toneCls}`} data-testid={testId}>
            <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
            <div className="mt-1 text-xl font-bold">{value}</div>
        </div>
    );
}

function ExpenseForm({ site, expense, vendors, categories, onClose, onSaved, onVendorsChanged, defaultMonth }) {
    const isEdit = !!expense;
    const today = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState(() => ({
        date: expense?.date || today,
        vendor_id: expense?.vendor_id || "",
        invoice_number: expense?.invoice_number || "",
        invoice_date: expense?.invoice_date || "",
        category_id: expense?.category_id || "",
        description: expense?.description || "",
        quantity: expense?.quantity ?? "",
        unit: expense?.unit || "",
        amount: expense?.amount ?? "",
        gst_included: !!expense?.gst_included,
        gst_amount: expense?.gst_amount ?? "",
        remarks: expense?.remarks || "",
        invoice_file_id: expense?.invoice_file_id || "",
        invoice_pending: !!expense?.invoice_pending,
        is_recoverable: !!expense?.is_recoverable,
    }));
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [duplicate, setDuplicate] = useState(null);
    const [showQuickVendor, setShowQuickVendor] = useState(false);

    async function uploadInvoice(file) {
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("site_id", site.id);
            fd.append("file", file);
            const { data } = await api.post("/expense-invoice-files", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((f) => ({ ...f, invoice_file_id: data.id, invoice_pending: false }));
            toast.success("Invoice uploaded");
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setUploading(false);
        }
    }

    async function submit(allow_duplicate = false) {
        setSaving(true);
        try {
            const payload = {
                ...form,
                site_id: site.id,
                amount: parseFloat(form.amount || 0),
                quantity: form.quantity === "" ? null : parseFloat(form.quantity),
                gst_amount: form.gst_amount === "" ? null : parseFloat(form.gst_amount),
                invoice_number: form.invoice_number || null,
                invoice_date: form.invoice_date || null,
                unit: form.unit || null,
                remarks: form.remarks || null,
                invoice_file_id: form.invoice_file_id || null,
                allow_duplicate,
            };
            if (isEdit) {
                await api.patch(`/expenses/${expense.id}`, payload);
                toast.success("Saved");
            } else {
                await api.post(`/sites/${site.id}/expenses`, payload);
                toast.success("Expense added");
            }
            onSaved();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.error === "duplicate") {
                setDuplicate(detail.existing);
            } else {
                toast.error(apiError(e));
            }
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="expense-form-modal">
            <div className="w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white flex items-center justify-between p-4 border-b">
                    <div className="font-display font-bold text-lg text-[#1E3355]">{isEdit ? "Edit expense" : "New expense"}</div>
                    <button onClick={onClose} className="text-gray-500 hover:text-black" data-testid="expense-form-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-4 space-y-3">
                    <Row2>
                        <Field label="Date">
                            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={fieldCls} data-testid="ef-date" />
                        </Field>
                        <Field label="Amount ₹">
                            <input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={fieldCls} data-testid="ef-amount" />
                        </Field>
                    </Row2>
                    <Field label="Vendor">
                        <div className="flex gap-2">
                            <select value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })} className={fieldCls} data-testid="ef-vendor">
                                <option value="">— select —</option>
                                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}{!v.verified ? " (unverified)" : ""}</option>)}
                            </select>
                            <button type="button" onClick={() => setShowQuickVendor(true)} className="rounded-md border border-gray-200 px-2 py-2 text-sm hover:bg-gray-50" data-testid="ef-quickadd-vendor">
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                    </Field>
                    <Row2>
                        <Field label="Invoice number">
                            <input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className={fieldCls} placeholder="Optional" data-testid="ef-invnum" />
                        </Field>
                        <Field label="Invoice date">
                            <input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} className={fieldCls} data-testid="ef-invdate" />
                        </Field>
                    </Row2>
                    <Field label="Category">
                        <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className={fieldCls} data-testid="ef-category">
                            <option value="">— select —</option>
                            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </Field>
                    <Field label="Description">
                        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={fieldCls} data-testid="ef-desc" placeholder="e.g. Pantry edibles — tea/coffee/milk/sugar" />
                    </Field>
                    <Row2>
                        <Field label="Qty (optional)">
                            <input type="number" min="0" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className={fieldCls} data-testid="ef-qty" />
                        </Field>
                        <Field label="Unit">
                            <input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className={fieldCls} placeholder="jar / pack …" data-testid="ef-unit" />
                        </Field>
                    </Row2>
                    <Row2>
                        <Field label="GST included?">
                            <select value={form.gst_included ? "yes" : "no"} onChange={(e) => setForm({ ...form, gst_included: e.target.value === "yes" })} className={fieldCls} data-testid="ef-gstincl">
                                <option value="no">No</option><option value="yes">Yes</option>
                            </select>
                        </Field>
                        <Field label="GST amount ₹ (optional)">
                            <input type="number" min="0" step="0.01" value={form.gst_amount} onChange={(e) => setForm({ ...form, gst_amount: e.target.value })} className={fieldCls} data-testid="ef-gstamt" />
                        </Field>
                    </Row2>
                    <Field label="Remarks">
                        <textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={2} className={fieldCls} data-testid="ef-remarks" />
                    </Field>
                    <div className="pt-2 border-t">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice file</div>
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                            <label className="rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer inline-flex items-center gap-2" data-testid="ef-upload-label">
                                <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : (form.invoice_file_id ? "Replace file" : "Upload PDF / image")}
                                <input type="file" className="hidden" accept="application/pdf,image/*" onChange={(e) => e.target.files?.[0] && uploadInvoice(e.target.files[0])} data-testid="ef-upload-input" />
                            </label>
                            {form.invoice_file_id && (
                                <a href={`${API}/expense-invoice-files/${form.invoice_file_id}/blob`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1E3355] hover:underline inline-flex items-center gap-1" data-testid="ef-view-invoice">
                                    <FileText className="w-4 h-4" /> View uploaded file
                                </a>
                            )}
                            <label className="inline-flex items-center gap-2 text-sm text-amber-700" data-testid="ef-pending-label">
                                <input type="checkbox" checked={form.invoice_pending} onChange={(e) => setForm({ ...form, invoice_pending: e.target.checked, invoice_file_id: e.target.checked ? "" : form.invoice_file_id })} data-testid="ef-pending" />
                                Invoice pending
                            </label>
                            <label className="inline-flex items-center gap-2 text-sm text-emerald-700 ml-auto" data-testid="ef-recover-label">
                                <input type="checkbox" checked={form.is_recoverable} onChange={(e) => setForm({ ...form, is_recoverable: e.target.checked })} data-testid="ef-recover" />
                                Recoverable from client
                            </label>
                        </div>
                    </div>
                    {duplicate && (
                        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-sm" data-testid="duplicate-warning">
                            <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Duplicate: same vendor + invoice number already exists.</div>
                            <div className="mt-1 text-xs">
                                Existing: {fmtDate(duplicate.date)} · {duplicate.site_name} · {duplicate.description} · ₹{Number(duplicate.amount).toLocaleString("en-IN")}
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                                <button onClick={() => submit(true)} disabled={saving} className="rounded-md bg-amber-600 text-white px-3 py-1.5 text-xs font-medium hover:opacity-90" data-testid="duplicate-proceed">Proceed anyway</button>
                                <button onClick={() => setDuplicate(null)} className="rounded-md border border-amber-300 px-3 py-1.5 text-xs font-medium" data-testid="duplicate-cancel">Cancel</button>
                            </div>
                        </div>
                    )}
                </div>
                <div className="sticky bottom-0 bg-white p-4 border-t flex items-center gap-2 justify-end">
                    <button onClick={onClose} className="rounded-md border border-gray-200 px-3 py-2 text-sm">Cancel</button>
                    <button onClick={() => submit(false)} disabled={saving || uploading} className="rounded-md bg-[#1E3355] text-white px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60" data-testid="ef-save">
                        {saving ? "Saving…" : (isEdit ? "Save changes" : "Add expense")}
                    </button>
                </div>
            </div>
            {showQuickVendor && (
                <QuickAddVendor
                    onClose={() => setShowQuickVendor(false)}
                    onCreated={async (v) => { await onVendorsChanged(); setForm({ ...form, vendor_id: v.id }); setShowQuickVendor(false); }}
                    categories={categories}
                />
            )}
        </div>
    );
}

const fieldCls = "w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3355] focus:border-transparent";
function Row2({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>; }
function Field({ label, children }) { return <div><div className="text-xs text-gray-500 mb-1">{label}</div>{children}</div>; }

function QuickAddVendor({ onClose, onCreated, categories }) {
    const [name, setName] = useState("");
    const [categoryName, setCategoryName] = useState("");
    const [saving, setSaving] = useState(false);
    async function save() {
        setSaving(true);
        try {
            const { data } = await api.post("/vendors", { name, categories: categoryName ? [categoryName] : [] });
            toast.success("Vendor added — HO will verify");
            onCreated(data);
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    }
    return (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="quick-vendor-modal">
            <div className="bg-white rounded-xl max-w-sm w-full p-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="font-bold text-[#1E3355]">Quick-add vendor</div>
                    <button onClick={onClose}><X className="w-4 h-4" /></button>
                </div>
                <Field label="Vendor name"><input value={name} onChange={(e) => setName(e.target.value)} className={fieldCls} data-testid="qav-name" /></Field>
                <div className="mt-2"><Field label="Category (optional)">
                    <select value={categoryName} onChange={(e) => setCategoryName(e.target.value)} className={fieldCls} data-testid="qav-cat">
                        <option value="">—</option>
                        {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                </Field></div>
                <button onClick={save} disabled={!name.trim() || saving} className="mt-3 w-full rounded-md bg-[#1E3355] text-white px-3 py-2 text-sm font-medium disabled:opacity-60" data-testid="qav-save">
                    {saving ? "Saving…" : "Add"}
                </button>
                <div className="mt-2 text-[11px] text-gray-500">Quick-adds are flagged unverified for HO to complete.</div>
            </div>
        </div>
    );
}
